import type { WidgetConfig } from "./client";
import { lookCss } from "./themes";

/**
 * The widget's stylesheet.
 *
 * Injected into a shadow root, which is why it can use short class names and
 * bare element selectors without any risk of touching the host page — and why
 * the host page's own CSS cannot reach in and break the widget, which is the
 * more common direction of that problem.
 *
 * Everything is driven by custom properties set from the site's config, so a
 * colour change is a data change rather than a rebuild.
 */
export function styles(config: WidgetConfig): string {
  const side = config.position === "bottom-left" ? "left" : "right";

  // The base sheet below is the "classic" look, byte for byte what sites had
  // before presets; lookCss appends a preset and any fine-tuning after it, and
  // appends nothing for an untouched classic config.
  return (
    `
:host {
  --cc-primary: ${config.primaryColor};
  --cc-on-primary: ${config.onPrimaryColor};
  --cc-bg: #ffffff;
  --cc-surface: #f6f7f9;
  --cc-text: #14171a;
  --cc-muted: #6b7280;
  --cc-border: #e5e7eb;
  --cc-shadow: 0 12px 40px rgba(15, 23, 42, 0.18);
  --cc-radius: 16px;

  position: fixed;
  ${side}: ${config.offsetX}px;
  bottom: ${config.offsetY}px;
  z-index: 2147483000;

  /*
   * Shadow DOM stops the host page's selectors from reaching in, but it does
   * NOT stop inheritance: font-style, letter-spacing, text-transform and the
   * rest flow through the host element into everything inside. A
   * site whose body is italic would otherwise render the whole widget in
   * italics. So every inheritable property is stated here rather than assumed.
   */
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  font-variant: normal;
  line-height: 1.5;
  letter-spacing: normal;
  word-spacing: normal;
  text-transform: none;
  text-align: left;
  text-indent: 0;
  text-shadow: none;
  text-decoration: none;
  white-space: normal;
  color: var(--cc-text);
  cursor: auto;
  visibility: visible;
}

${
  config.theme === "dark"
    ? ":host {"
    : config.theme === "auto"
      ? "@media (prefers-color-scheme: dark) { :host {"
      : ":host(.never) {"
}
  --cc-bg: #14171a;
  --cc-surface: #1e2227;
  --cc-text: #f2f4f6;
  --cc-muted: #9aa3ad;
  --cc-border: #2b3138;
  --cc-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
${config.theme === "auto" ? "} }" : "}"}

*, *::before, *::after { box-sizing: border-box; }

button { font: inherit; color: inherit; cursor: pointer; }

.launcher {
  width: 56px; height: 56px; border-radius: 50%;
  border: none; background: var(--cc-primary); color: var(--cc-on-primary);
  box-shadow: var(--cc-shadow);
  display: flex; align-items: center; justify-content: center;
  transition: transform 160ms ease;
}
.launcher:hover { transform: scale(1.06); }
.launcher:focus-visible { outline: 3px solid var(--cc-primary); outline-offset: 3px; }

.badge {
  position: absolute; top: -2px; ${side}: -2px;
  min-width: 20px; height: 20px; padding: 0 6px;
  border-radius: 10px; background: #ef4444; color: #fff;
  font-size: 12px; font-weight: 600;
  display: flex; align-items: center; justify-content: center;
}

.panel {
  position: absolute; bottom: 72px; ${side}: 0;
  width: 380px; max-width: calc(100vw - 32px);
  height: 560px; max-height: calc(100vh - 120px);
  background: var(--cc-bg); border: 1px solid var(--cc-border);
  border-radius: var(--cc-radius); box-shadow: var(--cc-shadow);
  display: flex; flex-direction: column; overflow: hidden;
  animation: cc-in 180ms ease;
}

@keyframes cc-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }

@media (prefers-reduced-motion: reduce) {
  .panel { animation: none; }
  .launcher { transition: none; }
}

/* A phone has no room for a floating card; the panel takes the screen. */
@media (max-width: 480px) {
  .panel {
    position: fixed; inset: 0;
    width: 100vw; max-width: 100vw; height: 100dvh; max-height: 100dvh;
    border-radius: 0; border: none;
  }
}

.header {
  background: var(--cc-primary); color: var(--cc-on-primary);
  padding: 16px; display: flex; align-items: center; gap: 12px;
}
.header .avatar {
  width: 36px; height: 36px; border-radius: 50%;
  background: rgba(255,255,255,0.22);
  display: flex; align-items: center; justify-content: center;
  overflow: hidden; flex: none;
}
.header .avatar img { width: 100%; height: 100%; object-fit: cover; }
.header h2 { margin: 0; font-size: 16px; font-weight: 600; }
.header p { margin: 2px 0 0; font-size: 13px; opacity: 0.85; }
.header .close {
  margin-left: auto; background: none; border: none;
  width: 32px; height: 32px; border-radius: 8px; display: grid; place-items: center;
}
.header .close:hover { background: rgba(255,255,255,0.18); }

.status { display: flex; align-items: center; gap: 6px; font-size: 13px; opacity: 0.9; }
.dot { width: 8px; height: 8px; border-radius: 50%; background: #34d399; }
.dot.off { background: #9ca3af; }

.messages {
  flex: 1; overflow-y: auto; padding: 16px;
  display: flex; flex-direction: column; gap: 10px;
  background: var(--cc-surface);
}

.row { display: flex; }
.row.visitor { justify-content: flex-end; }
/* The width cap sits on the bubble's wrapper, not the bubble: a percentage
   max-width inside a shrink-to-fit parent resolves against the parent's own
   min-content, and with overflow-wrap: anywhere that is one letter — "selam"
   broke into "sel / am". */
.row > div { max-width: 78%; min-width: 0; }

.bubble {
  padding: 10px 14px; border-radius: 14px;
  background: var(--cc-bg); border: 1px solid var(--cc-border);
  /* A pasted stack trace must not stretch the panel off the screen. */
  overflow-wrap: anywhere; white-space: pre-wrap;
}
.row.visitor .bubble {
  background: var(--cc-primary); color: var(--cc-on-primary); border-color: transparent;
}
.row.visitor .bubble a { color: var(--cc-on-primary); }
.bubble.pending { opacity: 0.6; }
.bubble a { color: var(--cc-primary); }

.meta { font-size: 11px; color: var(--cc-muted); margin: 2px 4px 0; }
.row.visitor .meta { text-align: right; }

.row.agent { gap: 8px; align-items: flex-end; }
/* Sits beside the bubble column; the bottom margin lines it up with the bubble, not the time. */
.op-avatar {
  width: 28px; height: 28px; border-radius: 50%; flex: none; object-fit: cover; margin-bottom: 18px;
  display: inline-flex; align-items: center; justify-content: center;
  color: #fff; font-size: 13px; font-weight: 600;
}
.header .avatar .op-avatar { width: 100%; height: 100%; margin: 0; font-size: 16px; }

.quick-replies { display: flex; flex-wrap: wrap; gap: 8px; }
.quick-reply {
  border: 1px solid var(--cc-primary); color: var(--cc-primary); background: var(--cc-bg);
  border-radius: 999px; padding: 7px 14px; font: inherit; font-size: 14px; cursor: pointer;
}
.quick-reply:hover, .quick-reply:focus-visible { background: var(--cc-primary); color: var(--cc-on-primary); }

.faq { display: flex; flex-direction: column; gap: 6px; }
.faq-title { font-size: 12px; font-weight: 600; letter-spacing: .02em; text-transform: uppercase; color: var(--cc-muted); }
.faq-item { border: 1px solid var(--cc-border); border-radius: 12px; background: var(--cc-bg); }
.faq-item summary {
  cursor: pointer; list-style: none; padding: 10px 34px 10px 12px; font-size: 14px; font-weight: 500;
  position: relative; border-radius: 12px;
}
.faq-item summary::-webkit-details-marker { display: none; }
.faq-item summary::after {
  content: ""; position: absolute; right: 14px; top: 50%; width: 7px; height: 7px; margin-top: -5px;
  border-right: 2px solid var(--cc-muted); border-bottom: 2px solid var(--cc-muted); transform: rotate(45deg);
  transition: transform .15s;
}
.faq-item[open] summary::after { transform: rotate(-135deg); margin-top: -2px; }
.faq-item summary:focus-visible { outline: 2px solid var(--cc-primary); outline-offset: 2px; }
.faq-item p { margin: 0; padding: 0 12px 12px; font-size: 14px; line-height: 1.5; color: var(--cc-text); white-space: pre-wrap; }
.faq-more { margin: 4px 0 0; font-size: 13px; color: var(--cc-muted); }

.attachments { display: flex; flex-wrap: wrap; gap: 6px; }
.bubble + .attachments { margin-top: 4px; }
.row.visitor .attachments { justify-content: flex-end; }
.thumb {
  padding: 0; border: 1px solid var(--cc-border); border-radius: 12px; overflow: hidden;
  background: var(--cc-bg); cursor: zoom-in; display: block;
}
.thumb img { display: block; max-width: 220px; max-height: 240px; object-fit: cover; }
.file {
  display: inline-flex; align-items: center; gap: 8px; max-width: 240px; padding: 8px 12px;
  border: 1px solid var(--cc-border); border-radius: 12px; background: var(--cc-bg);
  color: var(--cc-text); font-size: 14px; text-decoration: none;
}
.file:hover, .file:focus-visible { border-color: var(--cc-primary); }
.file-icon { flex: none; color: var(--cc-primary); font-weight: 700; }
.file-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.progress { height: 4px; width: 160px; border-radius: 2px; background: var(--cc-border); margin: 6px 0 0 auto; overflow: hidden; }
.progress span { display: block; height: 100%; background: var(--cc-primary); transition: width .2s; }

.composer.drop { outline: 2px dashed var(--cc-primary); outline-offset: -6px; }
.attach {
  border: none; background: none; color: var(--cc-muted); cursor: pointer;
  padding: 6px; border-radius: 8px; display: inline-flex; align-self: center;
}
.attach:hover, .attach:focus-visible { color: var(--cc-primary); background: var(--cc-surface); }

.viewer {
  position: fixed; inset: 0; background: rgba(0,0,0,.82); display: flex;
  align-items: center; justify-content: center; z-index: 10; cursor: zoom-out;
}
.viewer img { max-width: 92vw; max-height: 88vh; border-radius: 8px; cursor: default; }
.viewer-close {
  position: absolute; top: 12px; right: 16px; font-size: 30px; line-height: 1; color: #fff;
  background: none; border: none; cursor: pointer;
}
@media (prefers-reduced-motion: reduce) { .progress span { transition: none; } }

/* A proactive message, shown beside the launcher until the visitor opens the chat or dismisses it. */
.teaser {
  position: absolute; bottom: 72px; ${side}: 0; width: 280px; max-width: calc(100vw - 32px);
  display: flex; align-items: flex-start; gap: 4px;
  background: var(--cc-bg); color: var(--cc-text); border: 1px solid var(--cc-border);
  border-radius: 14px; box-shadow: var(--cc-shadow); padding: 4px;
  animation: cc-in 180ms ease;
}
.teaser-body {
  flex: 1; text-align: left; border: none; background: none; padding: 10px 6px 10px 10px;
  font-size: 14px; line-height: 1.45; cursor: pointer;
}
.teaser-close {
  border: none; background: none; color: var(--cc-muted); font-size: 18px; line-height: 1;
  width: 28px; height: 28px; border-radius: 8px;
}
.teaser-close:hover { background: var(--cc-surface); }
@media (prefers-reduced-motion: reduce) { .teaser { animation: none; } }

.offline-band {
  padding: 8px 16px; font-size: 13px; color: var(--cc-text);
  background: var(--cc-surface); border-bottom: 1px solid var(--cc-border);
}
.offline { flex: 1; overflow-y: auto; padding: 18px 16px; display: flex; flex-direction: column; gap: 10px; background: var(--cc-surface); }
.offline strong { font-size: 16px; }
.offline p { margin: 0; color: var(--cc-muted); font-size: 14px; }
.offline label { display: flex; flex-direction: column; gap: 4px; font-size: 13px; color: var(--cc-muted); }
.offline .muted { color: var(--cc-muted); }
.offline input, .offline textarea {
  font: inherit; color: var(--cc-text); background: var(--cc-bg);
  border: 1px solid var(--cc-border); border-radius: 10px; padding: 9px 11px; resize: vertical;
}
.offline input:focus, .offline textarea:focus { outline: 2px solid var(--cc-primary); outline-offset: -1px; }
.offline-send {
  margin-top: 4px; border: none; border-radius: 10px; padding: 10px 14px; font-weight: 600;
  background: var(--cc-primary); color: var(--cc-on-primary);
}
.offline-send:disabled { opacity: 0.5; cursor: default; }
.offline.done { justify-content: center; align-items: center; text-align: center; }

.ended-box { align-self: center; display: flex; flex-direction: column; align-items: center; gap: 8px; }
.new-chat {
  border: 1px solid var(--cc-border); background: var(--cc-bg); color: var(--cc-text);
  border-radius: 999px; padding: 7px 14px; font: inherit; font-size: 13px; font-weight: 600;
}
.new-chat:hover { border-color: var(--cc-primary); }
.system {
  align-self: center; font-size: 13px; color: var(--cc-muted);
  background: var(--cc-bg); border: 1px solid var(--cc-border);
  padding: 6px 12px; border-radius: 999px;
}

.typing { display: flex; gap: 4px; padding: 10px 14px; }
.typing span {
  width: 7px; height: 7px; border-radius: 50%; background: var(--cc-muted);
  animation: cc-blink 1.2s infinite;
}
.typing span:nth-child(2) { animation-delay: 0.2s; }
.typing span:nth-child(3) { animation-delay: 0.4s; }
@keyframes cc-blink { 0%, 60%, 100% { opacity: 0.3; } 30% { opacity: 1; } }

.composer {
  border-top: 1px solid var(--cc-border); background: var(--cc-bg);
  padding: 10px; display: flex; gap: 8px; align-items: flex-end;
}
.composer textarea {
  flex: 1; resize: none; border: 1px solid var(--cc-border); border-radius: 12px;
  padding: 10px 12px; font: inherit; color: inherit; background: var(--cc-surface);
  max-height: 120px; min-height: 42px;
}
.composer textarea:focus { outline: 2px solid var(--cc-primary); outline-offset: -1px; }
.composer button.send {
  width: 42px; height: 42px; border-radius: 12px; border: none;
  background: var(--cc-primary); color: var(--cc-on-primary);
  display: grid; place-items: center; flex: none;
}
.composer button.send:disabled { opacity: 0.45; cursor: default; }

.offline, .prechat, .rating { padding: 16px; display: flex; flex-direction: column; gap: 10px; }
.offline p, .prechat p { margin: 0; color: var(--cc-muted); font-size: 14px; }
.field { display: flex; flex-direction: column; gap: 4px; }
.field label { font-size: 13px; color: var(--cc-muted); }
.field input, .offline textarea {
  border: 1px solid var(--cc-border); border-radius: 10px; padding: 10px 12px;
  font: inherit; color: inherit; background: var(--cc-surface);
}
.primary {
  border: none; border-radius: 10px; padding: 11px 14px;
  background: var(--cc-primary); color: var(--cc-on-primary); font-weight: 600;
}
.primary:disabled { opacity: 0.5; cursor: default; }
.prechat { flex: 1; overflow-y: auto; background: var(--cc-surface); }
.prechat strong { font-size: 16px; }
.prechat .field input { background: var(--cc-bg); color: var(--cc-text); }
.field input:focus { outline: 2px solid var(--cc-primary); outline-offset: -1px; }
.ask {
  align-self: stretch; display: flex; flex-direction: column; gap: 8px;
  padding: 12px; border: 1px solid var(--cc-border); border-radius: 12px;
  background: var(--cc-bg); font-size: 13px; color: var(--cc-text);
}
.ask p { margin: 0; }
.ask-actions { display: flex; gap: 8px; align-items: center; }
.ask-actions .primary { padding: 8px 14px; }
.ask-skip { border: none; background: none; color: var(--cc-muted); font: inherit; padding: 8px 6px; text-decoration: underline; }
.stars { display: flex; gap: 6px; }
.stars button { background: none; border: none; font-size: 26px; line-height: 1; color: #f59e0b; padding: 0; }

.error {
  margin: 8px 16px; padding: 8px 12px; border-radius: 10px;
  background: #fef2f2; color: #991b1b; font-size: 13px;
}

.footer { text-align: center; font-size: 11px; color: var(--cc-muted); padding: 0 0 8px; }
.footer a { color: inherit; text-decoration: none; }
/*
 * Not --cc-primary: a site's chosen (or a preset's suggested) primary colour
 * is picked for its brand feel, not for its contrast against a light or dark
 * surface — neon's suggested cyan and brutal's suggested yellow both fail
 * WCAG AA as text here. --cc-text and --cc-muted are the two colours every
 * preset already has to keep readable against the panel (see the "contrast"
 * describe block in tests/presets.test.ts), so the link only ever uses those.
 */
.footer a:hover, .footer a:focus-visible { color: var(--cc-text); text-decoration: underline; }
.footer a:focus-visible { outline: 2px solid var(--cc-primary); outline-offset: 2px; border-radius: 2px; }
` + lookCss(config, side)
  );
}
